import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useUser } from "../../users/providers/UserProvider";
import {
  fetchWishlistIds,
  fetchWishlist,
  addToWishlist as apiAdd,
  removeFromWishlist as apiRemove,
  addBulkToWishlist as apiBulkAdd,
} from "../services/wishlistAPI";
import { useSnack } from "../../providers/SnackBarProvider";
import { useTradeActions } from "../../trades/providers/TradeActionProvider";

const WishlistContext = createContext(null);

/**
 * How many card ids may travel in one POST /wishlist/batch.
 *
 * The server refuses anything larger than 200 before it touches the database
 * (`server/wishlist/wishlistController.js` — "Maximum 200 cards per batch"), and
 * 200+-card sets are routine on the pokemontcg.io catalog the Sets tab renders.
 * So "Add all 246 missing to wishlist" used to post 246 ids and add none of
 * them (P2-56). Must stay at or below the server's cap; the client splits the
 * work rather than sending a request the server is guaranteed to reject.
 */
export const WISHLIST_BATCH_SIZE = 200;

const chunkIds = (ids, size) => {
  const chunks = [];
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
  return chunks;
};

/** The API layer rejects with a string; be tolerant of an Error slipping through. */
const errorText = (err, fallback) =>
  (typeof err === "string" ? err : err?.message) || fallback;

export const WishlistProvider = ({ children }) => {
  const { user } = useUser();
  // The account this provider currently serves. A reply for any other
  // account (signed out, or switched while it was in flight) is dropped:
  // it must not write that account's data into this session.
  // (Code review SEPARATION-7)
  const liveUserIdRef = useRef(user?._id ?? null);
  liveUserIdRef.current = user?._id ?? null;
  const snack = useSnack();

  // Full populated wishlist items (for the wishlist page)
  const [wishlistItems, setWishlistItems] = useState([]);
  // Set of card ID strings for O(1) per-card heart checks
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [wishlistCountKnown, setWishlistCountKnown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Card ids whose toggle request is still open. Keyed per id so a second tap
  // on the SAME heart is dropped while a different card can still be toggled.
  const inFlightIds = useRef(new Set());

  /**
   * Re-read the id Set from the server.
   *
   * Extracted from the sign-in effect below so that a caller who knows the
   * server changed the wishlist BEHIND the client's back can ask for a resync.
   * Completing a trade is that case: `clearWishlistEntries`
   * (server/trades/tradeTransferService.js:69-72) runs `Wishlist.deleteMany`
   * for every delivered card, so the heart on a card you just received is
   * filled against a row that no longer exists. `loadFullWishlist` also
   * resyncs the ids (:100), but it is the wishlist PAGE's loader — it sets
   * `isLoading` and replaces `wishlistItems`, which a trade page has no
   * business doing.
   *
   * Silent on failure, like the startup load: a stale heart is not worth a
   * snackbar on top of a trade the user just completed.
   */
  const refreshWishlistIds = useCallback(async () => {
    if (!user) return;
    const forUser = liveUserIdRef.current;
    try {
      const ids = await fetchWishlistIds();
      if (liveUserIdRef.current !== forUser) return;
      setWishlistIds(new Set(ids));
      setWishlistCountKnown(true);
    } catch (_) {
      // silent — non-critical
    }
  }, [user]);

  // Load IDs whenever user logs in / changes
  useEffect(() => {
    // Every account starts empty, not only a signed-out one: B must never see
    // A's hearts or items while B's own load is in flight. (SEPARATION-7)
    setWishlistIds(new Set());
    setWishlistItems([]);
    setWishlistCountKnown(false);
    if (!user) return;
    refreshWishlistIds();
  }, [user, refreshWishlistIds]);

  // A trade completing on the other phone deletes the wishlist rows for the
  // cards this collector received, same as on this one (B-56).
  const { completedCount } = useTradeActions();
  useEffect(() => {
    if (completedCount) refreshWishlistIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCount]);

  // Resolves `false` when the load failed, so the page can show a load error
  // instead of "Nothing saved yet" over an empty list (B-62). The flag itself
  // lives in WishlistPage, the only caller, which is a lazy chunk: state kept
  // there costs the main bundle nothing.
  //
  // `quiet === true` is a background reload (app return, error retry): no
  // skeleton and no snackbar, its own or the global interceptor's. Strictly
  // `true`, so a click event is a normal load.
  const loadFullWishlist = useCallback(async (quiet) => {
    if (!user) return;
    const loud = quiet !== true;
    try {
      if (loud) setIsLoading(true);
      const forUser = liveUserIdRef.current;
      const items = await fetchWishlist(loud ? undefined : { quiet });
      if (liveUserIdRef.current !== forUser) return false;
      setWishlistItems(items);
      // Keep IDs in sync
      setWishlistIds(new Set(items.map((i) => i.cardId?._id?.toString() ?? i.cardId?.toString())));
      setWishlistCountKnown(true);
      return true;
    } catch (err) {
      if (loud) snack("error", err || "Failed to load wishlist");
      return false;
    } finally {
      if (loud) setIsLoading(false);
    }
  }, [user, snack]);

  const toggleWishlist = useCallback(
    async (cardId, cardName) => {
      if (!user) {
        snack("error", "Please log in to use your wishlist");
        return;
      }
      const id = cardId.toString();
      // A double-tap lands while the first request is open, and the optimistic
      // update below has already flipped `wishlistIds` — so the second tap would
      // take the opposite branch and fire the opposite endpoint. Drop it.
      if (inFlightIds.current.has(id)) return;
      const alreadyWishlisted = wishlistIds.has(id);
      inFlightIds.current.add(id);

      // Optimistic update
      setWishlistIds((prev) => {
        const next = new Set(prev);
        alreadyWishlisted ? next.delete(id) : next.add(id);
        return next;
      });

      try {
        if (alreadyWishlisted) {
          await apiRemove(cardId);
          setWishlistItems((prev) =>
            prev.filter((item) => {
              const itemCardId = item.cardId?._id?.toString() ?? item.cardId?.toString();
              return itemCardId !== id;
            })
          );
          snack("info", `Removed ${cardName || "card"} from wishlist`);
        } else {
          await apiAdd(cardId);
          snack("success", `Added ${cardName || "card"} to wishlist`);
        }
      } catch (err) {
        // Revert optimistic update on failure
        setWishlistIds((prev) => {
          const next = new Set(prev);
          alreadyWishlisted ? next.add(id) : next.delete(id);
          return next;
        });
        snack("error", err || "Wishlist update failed");
      } finally {
        inFlightIds.current.delete(id);
      }
    },
    [user, wishlistIds, snack]
  );

  const isWishlisted = useCallback(
    (cardId) => wishlistIds.has(cardId?.toString()),
    [wishlistIds]
  );

  const addBulkToWishlist = useCallback(
    async (cardIds) => {
      if (!user) { snack("error", "Please log in to use your wishlist"); return null; }
      if (!cardIds?.length) return null;

      // Chunk here rather than at the call site, so every caller — the Sets tab
      // today, anything else tomorrow — inherits the split for free.
      const batches = chunkIds(cardIds, WISHLIST_BATCH_SIZE);
      let added = 0;
      let skipped = 0;

      for (const batch of batches) {
        try {
          const result = await apiBulkAdd(batch);
          added += result?.added ?? 0;
          skipped += result?.skipped ?? 0;
          // Commit each chunk as it lands. If a later one fails, the hearts
          // still show exactly what was written instead of nothing.
          setWishlistIds((prev) => {
            const next = new Set(prev);
            batch.forEach((id) => next.add(id.toString()));
            return next;
          });
        } catch (err) {
          const detail = errorText(err, "Failed to add cards to wishlist");
          // Never claim zero when earlier chunks landed, and never claim the
          // full count when they did not.
          snack(
            "error",
            added > 0
              ? `Added ${added} of ${cardIds.length} cards, then failed: ${detail}`
              : detail,
          );
          // Falsy still means "this did not fully succeed" — the partial total
          // is reported to the user, not smuggled back as a success shape.
          return null;
        }
      }

      snack("success", `Added ${added} card${added !== 1 ? "s" : ""} to wishlist`);
      return { added, skipped };
    },
    [user, snack],
  );

  const value = useMemo(
    () => ({
      wishlistItems,
      wishlistIds,
      wishlistCount: wishlistIds.size,
      // False until a load has actually answered for this account. Both loads
      // fail silently and leave the set empty, so without this a failed load
      // reads as a confident "0 cards saved" (tablet QA, versionCode 11).
      wishlistCountKnown,
      isLoading,
      toggleWishlist,
      isWishlisted,
      loadFullWishlist,
      refreshWishlistIds,
      addBulkToWishlist,
    }),
    [wishlistItems, wishlistIds, wishlistCountKnown, isLoading, toggleWishlist, isWishlisted, loadFullWishlist, refreshWishlistIds, addBulkToWishlist]
  );

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used inside WishlistProvider");
  return ctx;
};
